import { AnimatedNumber } from './AnimatedNumber';
import { PointsAmount, PointsText } from './PointsAmount';
import { RulesDialog } from './RulesDialog';
import { lazy, Suspense, useEffect, useState } from 'react';
import {
  ArrowUpRight,
  ArrowRight,
  Activity,
  Check,
  Trophy,
  X,
  UserRound,
} from 'lucide-react';
import type { Choice, OddsSelection } from '@pons/contracts';
import { Art, duration } from './arena-ui';
import { ArenaScreen } from './arena/ArenaScreen';
import { ExploreScreen } from './arena/ExploreScreen';
import { CoinModalProvider } from './arena/CoinModal';
import { exploreCoins, type ExploreFilter } from './arena/exploreModel';
import { TopBar } from './arena/TopBar';
import { AppFooter } from './arena/AppFooter';
import './arena/AppFooter.css';
import { useFeedSwitch } from './arena/useFeedSwitch';
import { useLiveArrivalNotice } from './arena/useLiveArrivalNotice';
import { LiveTokenNotice } from './arena/LiveTokenNotice';
import { InternalPanel, useInternalParams } from './arena/InternalPanel';
import { useSimulation } from './arena/useSimulation';
import { isCallable, type CoinView } from './arena/model';
import { useArena } from './useArena';
import { visibleRounds } from './market';
import { useAppNavigation } from './points-navigation';
import { PointsPage } from './reflex/PointsPage';
import { usePointsPreview } from './reflex/usePointsPreview';
import './reflex/points-host.css';
import { useGame } from './game/useGame';
import { useAccount } from './auth/AccountContext';
import { ProfileAccount } from './auth/ProfileAccount';
import { useAccountPoints } from './points/useAccountPoints';
import { HybridArt } from './game-preview/HybridArt';
import './game-preview/preview.css';
import './arena/AccountPages.css';
import './arena/AccountMenu.css';
const MockArena = lazy(() => import('./demo/MockArena'));
export default function App() {
  const account = useAccount();
  const game = useGame(account?.id);
  const {
    data,
    personal,
    leaders,
    now,
    ready,
    mode,
    notice,
    syncError,
    storageError,
    catchingUp,
    connection,
    setName,
    submit,
  } = useArena(account?.id);
  const [view, setView] = useAppNavigation();
  const { feed, selectedFeed, changeFeed } = useFeedSwitch(
    'live',
    view === 'Arena',
  );
  useEffect(() => {
    if (account && view === 'Profile') setView('Arena');
  }, [account, view, setView]);
  const [rules, setRules] = useState(false);
  const points = usePointsPreview();
  const accountPoints = useAccountPoints(account?.id, view === 'Points');
  const [exploreFilter, setExploreFilter] = useState<ExploreFilter>('Watching');
  const rounds = visibleRounds(data.rounds, now);
  const mine = personal;
  const finished = mine.filter((r) => r.outcome);
  const wins = finished.filter((r) => r.choice === r.outcome).length;
  const internal = useInternalParams();
  const simulation = useSimulation(
    internal.visible && internal.params.simulatedCoins,
    internal.params.simulatedTrading,
    now,
  );
  const coins = exploreCoins(rounds, personal, simulation.coins, now);
  const liveArrival = useLiveArrivalNotice({
    coins,
    now,
    enabled: mode !== 'mock' && selectedFeed === 'game',
    connected: ready && connection === 'connected' && !catchingUp,
  });
  const availablePredictions = {
    live:
      ready &&
      connection === 'connected' &&
      coins.some((coin) => isCallable(coin) && !coin.stale),
    game:
      game.ready && game.coins.some((coin) => isCallable(coin) && !coin.stale),
  };
  const explore = exploreCoins(data.rounds, personal, simulation.coins, now);
  const combined = [...coins, ...game.coins].sort((a, b) => b.start - a.start);
  const combinedExplore = [...explore, ...game.coins].sort(
    (a, b) => b.start - a.start,
  );
  const gameArt = new Map(game.rounds.map((round) => [round.id, round]));
  const roundById = new Map(
    [...data.rounds, ...personal].map((r) => [r.id, r]),
  );
  const artFor = (coin: CoinView, className?: string) => {
    const round = gameArt.get(coin.id);
    if (round)
      return round.hybrid ? (
        <HybridArt
          round={{ seed: round.visualSeed, image: round.image }}
          name={coin.name}
          className={className}
        />
      ) : (
        <img
          className={`token-art ${className || ''}`}
          src={round.image}
          alt={`${coin.name} · Game token`}
        />
      );
    return coin.simulated ? (
      simulation.art(coin, className)
    ) : (
      <Art
        symbol={coin.symbol}
        r={roundById.get(coin.id)}
        className={className}
      />
    );
  };
  const predict = (id: string, choice: Choice, odds?: OddsSelection) => {
    if (id.startsWith('game-')) return game.vote(id, choice, odds);
    return id.startsWith('sim-')
      ? simulation.call(id, choice)
      : submit(id, choice, odds);
  };
  const ranking = leaders.map((p) =>
    p.you ? { ...p, name: account?.displayName || data.name } : p,
  );
  if (mode === 'mock')
    return (
      <Suspense fallback={<div>Loading demo…</div>}>
        <MockArena />
      </Suspense>
    );
  const page = (
    <main
      className={
        'arena-app' +
        (view === 'Arena'
          ? ' has-arena'
          : view === 'Explore'
            ? ' has-explore'
            : '')
      }
    >
      <TopBar
        view={view}
        exploreFilter={exploreFilter}
        onExploreFilter={setExploreFilter}
        previewBalance={
          account
            ? accountPoints.formattedBalance
            : points.state.connected
              ? points.formattedBalance
              : '0'
        }
        accountPoints={!!account}
        feed={selectedFeed}
        onFeedChange={changeFeed}
        availablePredictions={availablePredictions}
        coins={combined}
        art={artFor}
        onPredictions={() => {
          setExploreFilter('My bets');
          setView('Explore');
        }}
        onView={setView}
        onProfile={() => setView('Profile')}
      />
      <LiveTokenNotice
        arrival={liveArrival.arrival}
        gameSelected={selectedFeed === 'game'}
        onDismiss={liveArrival.dismiss}
        onSwitchLive={() => {
          liveArrival.dismiss();
          changeFeed('live');
          setView('Arena');
        }}
      />
      <div className="app-notices">
        {game.error && (feed === 'game' || view === 'Explore') && (
          <p role="status" className="storage-error">
            {game.error}
          </p>
        )}
        {syncError && !(view === 'Arena' && feed === 'game') && (
          <p role="status" className="storage-error">
            {syncError}
          </p>
        )}
        {view === 'Explore' && catchingUp && (
          <p role="status">
            Catching up on recent graduations. Rounds will update automatically.
          </p>
        )}
        {storageError && (
          <p role="alert" className="storage-error">
            Your display name cannot be saved in this browser. Predictions are
            saved on the server.
          </p>
        )}
      </div>
      {notice && (
        <div className="toast" role="status">
          <Check size={18} />
          {notice}
        </div>
      )}
      {view === 'Points' ? (
        <div className="points-host">
          <PointsPage
            preview={points}
            account={account ? accountPoints : undefined}
            environment="live"
            goToArena={() => setView('Arena')}
          />
        </div>
      ) : view === 'Arena' ? (
        <ArenaScreen
          balance={accountPoints.data?.balance ?? null}
          coins={feed === 'game' ? game.coins : coins}
          onGame={
            feed === 'live'
              ? () => changeFeed(selectedFeed === 'game' ? 'live' : 'game')
              : undefined
          }
          gameSelected={selectedFeed === 'game'}
          sidebarCoins={combined}
          now={feed === 'game' ? game.now : now}
          ready={feed === 'game' ? game.ready : ready}
          art={artFor}
          onPredict={predict}
          onExplore={() => {
            setExploreFilter('Watching');
            setView('Explore');
          }}
          onPredictions={() => {
            setExploreFilter('My bets');
            setView('Explore');
          }}
          motion={internal.params.queue}
          railMotion={internal.params.rails}
        />
      ) : view === 'Explore' ? (
        <ExploreScreen
          coins={combinedExplore}
          sidebarCoins={combinedExplore}
          now={now}
          ready={ready}
          art={artFor}
          filter={exploreFilter}
          onFilter={setExploreFilter}
          onArena={() => setView('Arena')}
          motion={internal.params.queue}
          railMotion={internal.params.rails}
        />
      ) : (
        <div
          className={
            'app-shell' +
            (view === 'Profile' || view === 'Leaderboard'
              ? ' account-screen'
              : '')
          }
        >
          <section className="inner-page">
            {view !== 'Profile' && view !== 'Leaderboard' && (
              <span className="eyebrow">RUN OR DIE / SURVIVAL CLUB</span>
            )}
            <h1>{view}</h1>
            {view === 'My predictions' && (
              <>
                <div className="stat-grid">
                  <article>
                    <span>TOTAL CALLS</span>
                    <b>
                      <AnimatedNumber value={mine.length} />
                    </b>
                  </article>
                  <article>
                    <span>WAITING FOR RESULTS</span>
                    <b>
                      <AnimatedNumber value={mine.length - finished.length} />
                    </b>
                  </article>
                  <article>
                    <span>CORRECT CALLS</span>
                    <b className="positive">
                      <AnimatedNumber value={wins} />
                    </b>
                  </article>
                </div>
                {!mine.length ? (
                  <div className="empty">
                    <Activity />
                    <h2>Your first call starts here.</h2>
                    <p>Choose a token in the arena. Pick RUN! or DIE!.</p>
                    <button
                      className="primary"
                      onClick={() => setView('Arena')}
                    >
                      Enter the arena <ArrowUpRight size={17} />
                    </button>
                  </div>
                ) : (
                  <div className="prediction-list">
                    {mine.map((r) => (
                      <div className="prediction-row" key={r.id}>
                        <Art symbol={r.symbol} r={r} />
                        <div>
                          <h3>{r.name}</h3>
                          <small>
                            ${r.symbol} / {new Date(r.start).toLocaleString()}
                          </small>
                        </div>
                        <span
                          className={
                            r.choice === 'SURVIVES' ? 'positive' : 'negative'
                          }
                        >
                          {r.choice === 'SURVIVES' ? 'RUN!' : 'DIE!'}
                        </span>
                        <b>
                          <PointsText
                            text={
                              r.canonical === false
                                ? 'Rechecking chain data'
                                : !r.outcome
                                  ? now < r.settlesAt
                                    ? 'Result in ' + duration(r.settlesAt - now)
                                    : 'Pending verified data'
                                  : r.choice === r.outcome
                                    ? 'Correct / +100 pts'
                                    : 'Incorrect / +0 pts'
                            }
                          />
                        </b>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            {view === 'Leaderboard' && (
              <>
                {!ranking.length && (
                  <div className="empty">
                    <Trophy />
                    <h2>The first place is still open.</h2>
                    <p>
                      Make a prediction. Correct settled calls earn 100 points.
                    </p>
                    <button
                      className="primary"
                      onClick={() => setView('Arena')}
                    >
                      Back to the arena <ArrowUpRight size={16} />
                    </button>
                  </div>
                )}
                <div className="leaderboard">
                  {ranking.length > 0 && (
                    <div className="leader-columns" aria-hidden="true">
                      <span>Rank</span>
                      <span>Predictor</span>
                      <span>Prediction score</span>
                    </div>
                  )}
                  <div role="list" aria-label="Predictor rankings">
                    {ranking.map((p, i) => (
                      <div
                        role="listitem"
                        className={'leader-row ' + (p.you ? 'is-you' : '')}
                        key={p.id}
                      >
                        <span className="rank">
                          <AnimatedNumber
                            value={String(i + 1).padStart(2, '0')}
                          />
                        </span>
                        <span className="leader-avatar">{p.avatar}</span>
                        <div>
                          <h3>
                            {p.name} {p.you && <small>YOU</small>}
                          </h3>
                          <span>
                            {p.you ? 'Your position' : 'Live predictions'}
                          </span>
                        </div>
                        <b>
                          <PointsAmount value={p.score.toLocaleString()} />
                        </b>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
            {view === 'Profile' && (
              <>
                <div className="profile-grid">
                  <article className="profile-panel">
                    <div className="profile-card-heading">
                      <span className="profile-avatar">
                        <UserRound size={24} />
                      </span>
                      <div>
                        <h2>Account</h2>
                      </div>
                    </div>
                    {account ? (
                      <ProfileAccount />
                    ) : (
                      <>
                        <label>
                          Display name
                          <input
                            value={data.name}
                            maxLength={30}
                            onChange={(e) => setName(e.target.value)}
                          />
                        </label>
                        <p>Saved in this browser automatically.</p>
                      </>
                    )}
                  </article>
                  <div className="profile-overview">
                    <div className="stat-grid">
                      <div className="profile-empty-stat" aria-hidden="true" />
                      <article>
                        <span>Live accuracy</span>
                        <b>
                          <AnimatedNumber
                            value={
                              finished.length
                                ? Math.round((wins / finished.length) * 100) +
                                  '%'
                                : '—'
                            }
                          />
                        </b>
                        <p>
                          {wins} correct / {finished.length} settled
                        </p>
                      </article>
                    </div>
                    <div className="profile-empty-panel" aria-hidden="true" />
                  </div>
                </div>
              </>
            )}
          </section>
          {view !== 'Profile' && view !== 'Leaderboard' && (
            <footer className="site-footer">
              <span className="footer-brand">pons.</span>
              <p>
                Good instincts.
                <br />
                Better calls.
              </p>
              <span>
                PONS V2 / ROBINHOOD CHAIN
                <br />
                On-chain tokens / Predictions saved on the server
              </span>
              <button onClick={() => setRules(true)}>
                The rules <ArrowUpRight size={16} />
              </button>
            </footer>
          )}
        </div>
      )}
      {rules && (
        <RulesDialog onClose={() => setRules(false)}>
          <button
            className="close"
            aria-label="Close rules"
            onClick={() => setRules(false)}
          >
            <X />
          </button>
          <span className="eyebrow">NO STAKES. JUST INSTINCT.</span>
          <h2 id="rules-title">
            Three steps.
            <br />
            One good call.
          </h2>
          <ol>
            <li>
              <b>Spot the graduation.</b>
              <p>
                A token enters the arena when Pons V2 creates its graduated
                pool.
              </p>
            </li>
            <li>
              <b>Make your call in 20 seconds.</b>
              <p>
                Choose RUN! or DIE!. Your choice locks immediately and reveals
                the community split.
              </p>
            </li>
            <li>
              <b>See what survived.</b>
              <p>
                After 5 minutes: at least 50% USD price retention and one
                external pool swap means RUN!. Correct calls earn 100 points.
              </p>
            </li>
          </ol>
          <p className="fineprint">
            New rounds use USD prices; earlier rounds keep their original
            pair-based rules. Missing historical USD prices or incomplete
            activity leave the result pending. Predictions are saved on the
            server for your account.
          </p>
          <button className="primary" onClick={() => setRules(false)}>
            Got it. Let&apos;s play <ArrowRight size={16} />
          </button>
        </RulesDialog>
      )}
      {internal.visible && (
        <InternalPanel
          params={internal.params}
          onChange={internal.setParams}
          simulatedCount={simulation.coins.length}
          onCreateToken={(name, symbol) => {
            simulation.create(name, symbol);
          }}
          onClearAll={simulation.clear}
        />
      )}
      <AppFooter />
    </main>
  );
  return (
    <CoinModalProvider coins={combinedExplore} now={now} art={artFor}>
      {page}
    </CoinModalProvider>
  );
}
